import { forwardRef, useState, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Avatar,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Switch,
  Badge,
  Paper,
  Divider,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { DateTimePicker, DatePickerInput } from '@mantine/dates';
import { IconClock, IconMapPin, IconUsers, IconVideo, IconLink, IconRepeat, IconBell, IconPlus, IconX } from '@tabler/icons-react';
import { Button, Card } from '../../ui';
import { FadeIn } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface Attendee {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  required?: boolean;
  status?: 'pending' | 'accepted' | 'declined' | 'tentative';
}

export interface MeetingData {
  title: string;
  description?: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  location?: string;
  meetingLink?: string;
  attendees: Attendee[];
  recurrence?: {
    type: 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';
    interval?: number;
    endDate?: Date;
    daysOfWeek?: number[];
  };
  reminders?: {
    type: 'email' | 'notification';
    time: number; // minutes before
  }[];
  category?: string;
  isPrivate?: boolean;
}

export interface MeetingSchedulerProps {
  initialData?: Partial<MeetingData>;
  onSubmit: (data: MeetingData) => void | Promise<void>;
  onCancel?: () => void;
  availableAttendees?: Attendee[];
  categories?: { value: string; label: string; color: string }[];
  loading?: boolean;
  mode?: 'create' | 'edit';
  showVideoOptions?: boolean;
  className?: string;
}

const REMINDER_OPTIONS = [
  { value: '0', label: 'At time of event' },
  { value: '5', label: '5 minutes before' },
  { value: '15', label: '15 minutes before' },
  { value: '30', label: '30 minutes before' },
  { value: '60', label: '1 hour before' },
  { value: '1440', label: '1 day before' },
];

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

export const MeetingScheduler = forwardRef<HTMLDivElement, MeetingSchedulerProps>(
  (
    {
      initialData = {},
      onSubmit,
      onCancel,
      availableAttendees = [],
      categories = [],
      loading = false,
      mode = 'create',
      showVideoOptions = true,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [formData, setFormData] = useState<Partial<MeetingData>>({
      title: '',
      description: '',
      start: new Date(),
      end: new Date(Date.now() + 60 * 60 * 1000), // 1 hour from now
      allDay: false,
      location: '',
      meetingLink: '',
      attendees: [],
      recurrence: { type: 'none' },
      reminders: [{ type: 'notification', time: 15 }],
      category: '',
      isPrivate: false,
      ...initialData,
    });

    const [selectedAttendeeIds, setSelectedAttendeeIds] = useState<string[]>(
      initialData.attendees?.map((a) => a.id) || []
    );

    const handleChange = <K extends keyof MeetingData>(key: K, value: MeetingData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleStartChange = (date: Date | null) => {
      if (!date) return;
      handleChange('start', date);
      // Automatically adjust end time to maintain duration
      if (formData.end && formData.start) {
        const duration = formData.end.getTime() - formData.start.getTime();
        handleChange('end', new Date(date.getTime() + duration));
      }
    };

    const handleAttendeesChange = (ids: string[]) => {
      setSelectedAttendeeIds(ids);
      const attendees = availableAttendees
        .filter((a) => ids.includes(a.id))
        .map((a) => ({ ...a, status: 'pending' as const }));
      handleChange('attendees', attendees);
    };

    const addReminder = () => {
      const newReminders = [...(formData.reminders || []), { type: 'notification' as const, time: 15 }];
      handleChange('reminders', newReminders);
    };

    const removeReminder = (index: number) => {
      const newReminders = formData.reminders?.filter((_, i) => i !== index) || [];
      handleChange('reminders', newReminders);
    };

    const handleSubmit = () => {
      if (!formData.title || !formData.start || !formData.end) return;
      onSubmit(formData as MeetingData);
    };

    const getAttendeeStatusColor = (status?: string) => {
      switch (status) {
        case 'accepted':
          return 'green';
        case 'declined':
          return 'red';
        case 'tentative':
          return 'yellow';
        default:
          return 'gray';
      }
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="lg">
            <Text size="lg" fw={600} style={accessibilityStyles}>
              {mode === 'create' ? 'Schedule Meeting' : 'Edit Meeting'}
            </Text>

            {/* Title */}
            <TextInput
              label="Title"
              placeholder="Meeting title"
              required
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />

            {/* Date and Time */}
            <Group grow>
              <DateTimePicker
                label="Start"
                placeholder="Select start date and time"
                value={formData.start}
                onChange={handleStartChange}
                required
              />
              <DateTimePicker
                label="End"
                placeholder="Select end date and time"
                value={formData.end}
                onChange={(date) => date && handleChange('end', date)}
                minDate={formData.start}
                required
              />
            </Group>

            <Switch
              label="All day event"
              checked={formData.allDay}
              onChange={(e) => handleChange('allDay', e.currentTarget.checked)}
            />

            {/* Location */}
            <TextInput
              label="Location"
              placeholder="Add location"
              leftSection={<IconMapPin size={16} />}
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
            />

            {/* Video Meeting */}
            {showVideoOptions && (
              <TextInput
                label="Meeting Link"
                placeholder="Add video conferencing link"
                leftSection={<IconVideo size={16} />}
                value={formData.meetingLink}
                onChange={(e) => handleChange('meetingLink', e.target.value)}
              />
            )}

            <Divider />

            {/* Attendees */}
            <Box>
              <MultiSelect
                label="Attendees"
                placeholder="Add attendees"
                leftSection={<IconUsers size={16} />}
                data={availableAttendees.map((a) => ({
                  value: a.id,
                  label: a.name,
                }))}
                value={selectedAttendeeIds}
                onChange={handleAttendeesChange}
                searchable
                clearable
              />
              {formData.attendees && formData.attendees.length > 0 && (
                <Stack gap="xs" mt="sm">
                  {formData.attendees.map((attendee) => (
                    <Paper key={attendee.id} p="xs" withBorder>
                      <Group justify="space-between">
                        <Group gap="sm">
                          <Avatar src={attendee.avatar} size="sm" radius="xl">
                            {attendee.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Text size="sm" style={accessibilityStyles}>{attendee.name}</Text>
                            <Text size="xs" c="dimmed" style={accessibilityStyles}>{attendee.email}</Text>
                          </Box>
                        </Group>
                        <Badge size="xs" color={getAttendeeStatusColor(attendee.status)}>
                          {attendee.status || 'pending'}
                        </Badge>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            {/* Recurrence */}
            <Select
              label="Repeat"
              leftSection={<IconRepeat size={16} />}
              data={RECURRENCE_OPTIONS}
              value={formData.recurrence?.type || 'none'}
              onChange={(value) => {
                const nextType = (value as RecurrenceType) || 'none';
                handleChange('recurrence', { ...(formData.recurrence ?? { type: 'none' as RecurrenceType }), type: nextType });
              }}
            />

            {formData.recurrence?.type !== 'none' && (
              <DatePickerInput
                label="Repeat until"
                placeholder="Select end date"
                value={formData.recurrence?.endDate}
                onChange={(date) => {
                  const currentRecurrence = formData.recurrence ?? { type: 'none' };
                  handleChange('recurrence', { ...currentRecurrence, endDate: date || undefined });
                }}
                clearable
              />
            )}

            {/* Reminders */}
            <Box>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} style={accessibilityStyles}>Reminders</Text>
                <Tooltip label="Add reminder">
                  <ActionIcon size="sm" variant="subtle" onClick={addReminder}>
                    <IconPlus size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Stack gap="xs">
                {formData.reminders?.map((reminder, index) => (
                  <Group key={index} gap="sm">
                    <IconBell size={16} color="gray" />
                    <Select
                      data={REMINDER_OPTIONS}
                      value={String(reminder.time)}
                      onChange={(value) => {
                        const newReminders = [...(formData.reminders || [])];
                        newReminders[index] = { ...reminder, time: Number(value) };
                        handleChange('reminders', newReminders);
                      }}
                      size="xs"
                      style={{ flex: 1 }}
                    />
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={() => removeReminder(index)}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            </Box>

            <Divider />

            {/* Category */}
            {categories.length > 0 && (
              <Select
                label="Category"
                placeholder="Select category"
                data={categories.map((c) => ({ value: c.value, label: c.label }))}
                value={formData.category}
                onChange={(value) => handleChange('category', value || undefined)}
                clearable
              />
            )}

            {/* Description */}
            <Textarea
              label="Description"
              placeholder="Add meeting description or agenda"
              minRows={3}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />

            {/* Privacy */}
            <Switch
              label="Private meeting"
              description="Only invited attendees can see meeting details"
              checked={formData.isPrivate}
              onChange={(e) => handleChange('isPrivate', e.currentTarget.checked)}
            />

            {/* Actions */}
            <Group justify="flex-end" gap="sm">
              {onCancel && (
                <Button variant="default" onClick={onCancel} disabled={loading}>
                  Cancel
                </Button>
              )}
              <Button onClick={handleSubmit} loading={loading}>
                {mode === 'create' ? 'Schedule Meeting' : 'Save Changes'}
              </Button>
            </Group>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

MeetingScheduler.displayName = 'MeetingScheduler';

export default MeetingScheduler;
type RecurrenceType = NonNullable<MeetingData['recurrence']>['type'];
